import type { EmailVerifyResult } from '@/api/workflows';

import { normalizeEmail } from './emailVerifyGate';

/**
 * Every ZeroBounce verdict this page session has obtained, keyed by normalized address.
 *
 * <h3>Why this is a module singleton and not a ref</h3>
 * Both consumers used to hold their own `useRef` cache — {@link ./useEmailVerification}'s
 * attempted-set and {@link ./OneSpanESignStep}'s `cacheRef` — which meant the cache died with
 * the component. That is exactly the wrong lifetime for a METERED lookup: the e-signature
 * step's verify effect fires on mount, so every re-entry re-batched every signatory, and any
 * remount of the form shell re-asked about addresses we already had answers for. Mount count
 * is not a thing anyone should be billed for. Living at module scope is the whole point —
 * it is what makes a verdict outlive the component that fetched it.
 *
 * It also means the two subsystems now share: an address checked on an ordinary form field
 * is already known by the time the advisor reaches the e-signature step, and the other way
 * round. Same endpoint, same verdict semantics, same key, so there is nothing to reconcile.
 *
 * Deliberately in memory only — no `sessionStorage`, no `localStorage`. A reload re-verifies,
 * which is the accepted cost of keeping client email addresses out of any storage at rest.
 * The backend cache (see the follow-up) is the thing that closes the reload case properly.
 */
type CacheEntry = {
  result: EmailVerifyResult;
  /** `Date.now()` at the time the verdict was stored. */
  at: number;
};

const cache = new Map<string, CacheEntry>();

/**
 * How long a verdict is trusted.
 *
 * A verdict is not permanent — a mailbox can be deleted and a domain can lapse — so this is
 * not just a memory bound. In practice it bites only on a tab left open for days, which is
 * precisely the session where a stale `Valid` would be least defensible: this gates a signing
 * package going to OneSpan.
 */
export const VERDICT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * The stored verdict for an already-normalized address, or undefined if we have none.
 *
 * Expiry is enforced on READ rather than by a timer — no bookkeeping to schedule, and no way
 * for a sweep and the map to drift. `editorLock.ts` prunes on read for the same reason.
 */
export function readCachedVerdict(normalizedEmail: string): EmailVerifyResult | undefined {
  const hit = cache.get(normalizedEmail);
  if (!hit) return undefined;
  if (Date.now() - hit.at >= VERDICT_TTL_MS) {
    cache.delete(normalizedEmail);
    return undefined;
  }
  return hit.result;
}

/**
 * Store one verdict, keyed off its own `email` so a caller can hand over a raw response row.
 *
 * <h3>`Unknown` is never stored</h3>
 * `Unknown` is not a verdict, it is the ABSENCE of one wearing a name — ZeroBounce
 * unreachable, key missing from Secrets Manager, a 429 from our own rate limiter, a 4xx, or
 * an address the response simply omitted. Both call sites already refused to cache it; that
 * refusal matters more now the cache outlives a mount, because `Unknown` HARD-BLOCKS an
 * advisor on the e-signature step (see {@link ./esignEmailGate}). Storing one would pin that
 * block across every later visit, long after ZeroBounce recovered, and the re-entry that
 * would naturally have retried would be served the stale failure instead.
 *
 * Nothing is re-spent by skipping it: a failed lookup costs no credit.
 */
export function cacheVerdict(result: EmailVerifyResult): void {
  if (result.status === 'Unknown') return;
  cache.set(normalizeEmail(result.email), { result, at: Date.now() });
}

/**
 * Empty the cache. For tests only — a module singleton otherwise bleeds verdicts from one
 * case into the next. Wired into the global `beforeEach` in `src/test/setup.ts` so no
 * individual suite has to remember.
 */
export function resetEmailVerifyCache(): void {
  cache.clear();
}
